/**
 * MNSP service-type pricing lookup -- TS mirror.
 *
 * Mirrors infrastructure/lambdaFunctions/sharedModules/python/mnsp_pricing.py.
 * Used for the read-only pricing display + the live subtotal in
 * MnspEnrollmentSection. Values MUST stay in sync with the Python
 * source; there's no automated drift test today, treat as a two-file
 * edit when Michael updates pricing.
 *
 * Source of truth (Michael's 2026-06-23 reply to the questions email):
 *
 *   PDI MNSP, yes or no, if yes, PDI MNSP MONTHLY:
 *     Bulloch:  $159.50
 *     Verifone: $149.50
 *     Gilbarco: $149.50
 *   Deployment fee: $500.00 (all)
 */

import type { MnspServiceType, MnspShippingSpeed } from "./types";

export interface MnspFeeRow {
  displayRegister: string;
  monthlyFee: number;
  deploymentFee: number;
}

// Keyed only by the three fixed-price registers. OTHER_MNSP deliberately
// has no entry: it carries no fixed price (the rep confirms it manually),
// so getMnspFees returns undefined for it and callers render blank / "to
// be confirmed" cells.
export const MNSP_FEES: Record<
  Exclude<MnspServiceType, "OTHER_MNSP">,
  MnspFeeRow
> = {
  BULLOCH_MNSP: {
    displayRegister: "Bulloch BT9000",
    monthlyFee: 159.5,
    deploymentFee: 500.0,
  },
  VERIFONE_MNSP: {
    displayRegister: "Verifone C18",
    monthlyFee: 149.5,
    deploymentFee: 500.0,
  },
  GILBARCO_MNSP: {
    displayRegister: "Gilbarco Passport",
    monthlyFee: 149.5,
    deploymentFee: 500.0,
  },
};

export interface ShippingFeeRow {
  label: string;
  amount: number;
}

// TWO_DAY replaced THREE_DAY at the same $275 price. THREE_DAY is no longer
// offered (see SHIPPING_SPEED_OPTIONS) but stays here so applications saved
// with it still price and display the way they were sold. Mirrors
// _SHIPPING_FEES in mnsp_pricing.py.
export const SHIPPING_FEES: Record<MnspShippingSpeed, ShippingFeeRow> = {
  STANDARD: { label: "Standard (no charge)", amount: 0.0 },
  ONE_DAY: { label: "1day $375", amount: 375.0 },
  TWO_DAY: { label: "2day $275", amount: 275.0 },
  THREE_DAY: { label: "3day $275", amount: 275.0 },
  FIVE_DAY: { label: "5day $175", amount: 175.0 },
};

/** The shipping speeds offered for selection, in display order. */
export const SHIPPING_SPEED_OPTIONS: MnspShippingSpeed[] = [
  "STANDARD",
  "ONE_DAY",
  "TWO_DAY",
  "FIVE_DAY",
];

/**
 * The speeds to show in a picker whose current value is `current`: the offered
 * speeds, plus a legacy speed (THREE_DAY) when it's the saved selection, so an
 * old application shows what it was sold with instead of silently losing it.
 */
export function shippingSpeedChoices(
  current: MnspShippingSpeed | "" | null | undefined,
): MnspShippingSpeed[] {
  if (!current || SHIPPING_SPEED_OPTIONS.includes(current)) {
    return SHIPPING_SPEED_OPTIONS;
  }
  if (!(current in SHIPPING_FEES)) return SHIPPING_SPEED_OPTIONS;
  // Slot the legacy speed in by price so the list stays ordered.
  return [...SHIPPING_SPEED_OPTIONS, current].sort(
    (a, b) =>
      (a === "STANDARD" ? -Infinity : -SHIPPING_FEES[a].amount) -
      (b === "STANDARD" ? -Infinity : -SHIPPING_FEES[b].amount),
  );
}

/** Lookup with safe fallback to undefined (drives blank cells). */
export function getMnspFees(
  serviceType: MnspServiceType | "" | null | undefined,
): MnspFeeRow | undefined {
  if (!serviceType) return undefined;
  // OTHER_MNSP (and any unknown value) has no key here -> undefined.
  return (MNSP_FEES as Record<string, MnspFeeRow | undefined>)[serviceType];
}

export function getShippingFee(
  speed: MnspShippingSpeed | "" | null | undefined,
): ShippingFeeRow | undefined {
  if (!speed) return undefined;
  return SHIPPING_FEES[speed as MnspShippingSpeed];
}

/**
 * Effective shipping fee: the standard per-speed lookup, with the amount
 * replaced by the operator override -- but ONLY when `speed` matches
 * `shippingFeeOverrideSpeed`, the exact speed the override was set for.
 * This is a deliberate security boundary, not just a display nicety: a
 * custom price set while e.g. Standard was selected must NOT carry over
 * if the shipping speed is later changed to 1-day -- otherwise a
 * merchant could switch to a pricier expedited option and still only
 * pay the cheaper override amount. shippingSpeed itself stays freely
 * editable (by operator or merchant); only the override is pinned to
 * one specific speed. Falls back to the standard per-speed amount
 * whenever the speeds don't match, the override is absent, or speed is
 * missing. Mirrors the backend's get_effective_shipping_fee in
 * mnsp_pricing.py -- keep both in sync.
 */
export function getEffectiveShippingFee(
  speed: MnspShippingSpeed | "" | null | undefined,
  shippingFeeOverride?: number | null,
  shippingFeeOverrideSpeed?: MnspShippingSpeed | "" | null,
): ShippingFeeRow | undefined {
  const base = getShippingFee(speed);
  const overrideApplies =
    !!speed &&
    speed === shippingFeeOverrideSpeed &&
    shippingFeeOverride !== null &&
    shippingFeeOverride !== undefined;
  if (!overrideApplies) return base;
  return {
    label: mnspShippingToPdiLabel(speed),
    amount: shippingFeeOverride as number,
  };
}

/**
 * Display label for one shipping-speed option/button (e.g. the speed
 * picker's radio pills, or a read-only shipping summary), reflecting its
 * operator override ONLY when that override is pinned to this exact
 * speed -- see getEffectiveShippingFee. Used so a custom price is
 * visible right on the option it applies to, and never bleeds into a
 * different speed's button.
 */
export function getShippingOptionLabel(
  speed: MnspShippingSpeed,
  shippingFeeOverride?: number | null,
  shippingFeeOverrideSpeed?: MnspShippingSpeed | "" | null,
): string {
  const overrideApplies =
    speed === shippingFeeOverrideSpeed &&
    shippingFeeOverride !== null &&
    shippingFeeOverride !== undefined;
  if (!overrideApplies) return SHIPPING_FEES[speed].label;
  return `${mnspShippingToPdiLabel(speed)} ${formatUsd(shippingFeeOverride)} (custom)`;
}

/**
 * Effective fees for the selected register: the standard per-register
 * lookup, with either fee replaced by its operator override when present.
 * A finite override (including 0, for a full fee waiver) wins; null/
 * undefined falls back to the standard fee. Mirrors the backend's
 * get_effective_mnsp_fees in mnsp_pricing.py -- keep both in sync.
 */
export function getEffectiveMnspFees(
  serviceType: MnspServiceType | "" | null | undefined,
  deploymentFeeOverride?: number | null,
  monthlyFeeOverride?: number | null,
): MnspFeeRow | undefined {
  const base = getMnspFees(serviceType);
  if (!base) return undefined;
  return {
    displayRegister: base.displayRegister,
    deploymentFee:
      deploymentFeeOverride !== null && deploymentFeeOverride !== undefined
        ? deploymentFeeOverride
        : base.deploymentFee,
    monthlyFee:
      monthlyFeeOverride !== null && monthlyFeeOverride !== undefined
        ? monthlyFeeOverride
        : base.monthlyFee,
  };
}

/**
 * Maps the MNSP shipping-speed selection to the string sent to PDI as the
 * order's "Shipping" value. The PDI section no longer has its own shipping
 * dropdown; the MNSP selection is the single source of truth. PDI receives
 * this string verbatim (pdiOrderHandler passes it through unvalidated), so
 * we send a clean human label. Falls back to "Standard" for an empty or
 * unknown selection, which also covers applications with no MNSP bundle
 * (mnspForm defaults to STANDARD).
 */
export function mnspShippingToPdiLabel(
  speed: MnspShippingSpeed | "" | null | undefined,
): string {
  switch (speed) {
    case "ONE_DAY":
      return "1 Day";
    case "TWO_DAY":
      return "2 Day";
    case "THREE_DAY":
      return "3 Day";
    case "FIVE_DAY":
      return "5 Day";
    case "STANDARD":
    default:
      return "Standard";
  }
}

/**
 * Subtotal of fees due at packet delivery: deployment + first-month +
 * shipping. Matches the Python source's compute_fees_subtotal so the
 * UI subtotal === the rendered PDF's "Total Funds collected" cell.
 * Returns undefined when serviceType is missing.
 */
export function computeFeesSubtotal(
  serviceType: MnspServiceType | "" | null | undefined,
  shippingSpeed: MnspShippingSpeed | "" | null | undefined,
  deploymentFeeOverride?: number | null,
  monthlyFeeOverride?: number | null,
  shippingFeeOverride?: number | null,
  shippingFeeOverrideSpeed?: MnspShippingSpeed | "" | null,
): number | undefined {
  const fees = getEffectiveMnspFees(
    serviceType,
    deploymentFeeOverride,
    monthlyFeeOverride,
  );
  if (!fees) return undefined;
  const shipping = getEffectiveShippingFee(
    shippingSpeed,
    shippingFeeOverride,
    shippingFeeOverrideSpeed,
  ) ?? { amount: 0.0 };
  return fees.deploymentFee + fees.monthlyFee + shipping.amount;
}

/** USD format helper for the read-only display surface. */
const _usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return _usd.format(n);
}
